import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { waLink } from "@/lib/api";
import { ChevronDown, MessageCircle, Send, Scale, HandCoins, HeartHandshake } from "lucide-react";

const SECTIONS = [
  {
    icon: Scale,
    title: "I tuoi debiti",
    items: [
      {
        q: "Come faccio a pagare i debiti?",
        a: "Ci sono diverse strade e la prima consulenza serve proprio a scegliere quella giusta per te: il saldo e stralcio (paghi una somma ridotta e chiudi il debito), una rateizzazione concordata con i creditori, oppure le procedure della Legge 3/2012 come il piano del consumatore e l'esdebitazione. Non devi decidere da solo: raccontaci la tua situazione dal modulo Richiedi assistenza oppure prenota un colloquio gratuito.",
      },
      {
        q: "Quanto tempo ci vorrà per risolvere la mia situazione?",
        a: "Dipende dalla strada scelta: un saldo e stralcio può chiudersi in poche settimane o pochi mesi; un piano di ristrutturazione o l'esdebitazione richiedono in genere da alcuni mesi a oltre un anno, perché passano dal tribunale. Dopo la prima consulenza gratuita ti daremo una stima realistica per il tuo caso. Intanto ricorda: fare il primo passo ferma l'ansia e spesso blocca le azioni più pesanti dei creditori.",
      },
      {
        q: "Mi portano via la casa?",
        a: "Nella maggior parte dei casi no, e comunque mai dall'oggi al domani: la legge prevede tutele forti per l'abitazione principale, in particolare per i debiti con il fisco sotto certe soglie, e procedure come la Legge 3/2012 possono bloccare le vendite forzate. Prima agisci, più strumenti hai. Se hai già ricevuto atti (precetto, pignoramento, decreto ingiuntivo), contattaci subito su WhatsApp o prenota un colloquio urgente.",
      },
      {
        q: "Possono pignorarmi lo stipendio o la pensione?",
        a: "In genere può essere pignorato al massimo un quinto dello stipendio o della pensione, e per la pensione è sempre protetto il minimo vitale, una soglia che nessuno può toccare. Sul conto corrente devono inoltre restare somme pari al triplo dell'assegno sociale. Se hai ricevuto un atto di pignoramento, non ignorarlo: molti pignoramenti si possono ridurre o bloccare.",
      },
      {
        q: "Cosa succede ai garanti?",
        a: "Se qualcuno ha firmato come garante (fideiussore), il creditore può chiedere il pagamento anche a lui. Ma i garanti hanno diritti e difese specifiche, e in molte procedure di sovraindebitamento anche la loro posizione può essere affrontata. Se nella tua situazione c'è un garante, segnalalo subito: è un aspetto delicato che va valutato con un nostro operatore.",
      },
      {
        q: "Cos'è il saldo e stralcio?",
        a: "È un accordo con il creditore: paghi una somma inferiore al debito totale (a saldo) e il creditore rinuncia al resto (stralcio). Sconti anche del 40-60% sono possibili su debiti vecchi o difficili da riscuotere. Regola d'oro: mai pagare prima di avere per iscritto la rinuncia al debito residuo (la lettera liberatoria). Ti aiutiamo noi a impostare la proposta in modo sicuro.",
      },
      {
        q: "Cos'è l'esdebitazione?",
        a: "È la cancellazione legale dei debiti che non riesci a pagare, riservata al debitore meritevole: dopo aver messo a disposizione ciò che possiedi, il giudice può liberarti dai debiti residui. Si ricomincia davvero da zero. Verifichiamo gratuitamente se hai i requisiti.",
      },
      {
        q: "Il recupero crediti può chiamarmi a qualsiasi ora?",
        a: "No. Le società di recupero crediti non possono molestarti, chiamarti di notte, minacciarti o contattare familiari, vicini o datori di lavoro per metterti in imbarazzo. Puoi sempre chiedere per iscritto la documentazione che prova il debito. Conserva lettere e appunti delle telefonate: sono prove utili.",
      },
      {
        q: "I debiti vanno in prescrizione?",
        a: "Molti sì: diversi debiti si prescrivono dopo alcuni anni. Attenzione però: riconoscere il debito per iscritto o fare un pagamento fa ripartire i tempi. Prima di firmare o pagare qualsiasi cosa su un debito vecchio, faccelo verificare gratuitamente.",
      },
    ],
  },
  {
    icon: HeartHandshake,
    title: "L'associazione e l'assistenza",
    items: [
      {
        q: "L'assistenza è davvero gratuita?",
        a: "Sì, sempre e per tutti: informazione, prima consulenza, accompagnamento e — nei casi più gravi — supporto psicologico non costano nulla. Il nostro lavoro è reso possibile dal volontariato e dal sistema dell'8 per mille.",
      },
      {
        q: "Come mi iscrivo all'associazione?",
        a: "Dalla pagina Iscriviti gratis: bastano nome, cognome, email e città. L'iscrizione è e resterà sempre gratuita, i tuoi dati restano riservati e puoi cancellarti quando vuoi.",
      },
      {
        q: "Come prenoto un colloquio in sede?",
        a: "Dalla pagina Prenota scegli sede, giorno e ora: lo sportello è aperto dal lunedì al venerdì, dalle 9:00 alle 13:00, a Milano (Via Pordenone 13) e a Gallarate (Via Scipione Ronchetti 8). Per altri orari puoi richiedere un appuntamento specifico scrivendoci su WhatsApp.",
      },
      {
        q: "Come funziona il supporto psicologico?",
        a: "Il supporto psicologico gratuito è offerto grazie alla collaborazione della Dott.ssa V. Graffeo, psicologa professionista, ed è disponibile su appuntamento, previa valutazione della richiesta, attualmente per i casi di maggiore necessità. Funziona così: compili il modulo Richiedi assistenza raccontando brevemente la tua situazione e il disagio che stai vivendo (spunta la voce dedicata); l'associazione valuta la richiesta; poi ti ricontattiamo comunicandoti giorno e orario in cui verrai chiamato direttamente dalla psicologa. Non è una prenotazione automatica: sarà l'associazione a fissare il colloquio telefonico.",
      },
      {
        q: "I miei dati sono riservati?",
        a: "Sì. Tutto ciò che ci racconti resta riservato, non viene ceduto a nessuno e serve solo per aiutarti. Vale per il modulo di assistenza, per l'iscrizione, per i colloqui in sede e per la chat.",
      },
    ],
  },
  {
    icon: HandCoins,
    title: "L'8 per mille",
    items: [
      {
        q: "Cos'è l'8 per mille?",
        a: "È una quota delle imposte che lo Stato ti permette di destinare a finalità sociali con la dichiarazione dei redditi: basta una firma nell'apposito riquadro. I fondi raccolti finanziano il nostro sportello gratuito, l'assistenza di base e il supporto psicologico ai casi più gravi.",
      },
      {
        q: "Destinare l'8 per mille mi costa qualcosa?",
        a: "No: non è una tassa in più. Se non esprimi una scelta, quella quota viene comunque trattenuta e ripartita automaticamente. Firmare significa semplicemente decidere tu a chi destinarla.",
      },
    ],
  },
];

function FaqItem({ item, open, onToggle, testId }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden" data-testid={testId}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`${testId}-question`}
        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-brand-mist/60 transition-colors"
      >
        <span className="font-bold text-lg text-brand-ink">{item.q}</span>
        <span
          className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-[background-color,transform] duration-300 ${
            open ? "bg-brand-blue text-white rotate-180" : "bg-brand-mist text-brand-blue"
          }`}
        >
          <ChevronDown className="w-5 h-5" />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <p className="px-6 pb-6 text-lg text-slate-600 leading-relaxed border-t border-slate-100 pt-4" data-testid={`${testId}-answer`}>
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Faq() {
  const [openKey, setOpenKey] = useState("0-0");

  return (
    <div className="container-site py-16 lg:py-24 max-w-3xl" data-testid="faq-page">
      <Reveal className="text-center">
        <span className="eyebrow justify-center">Domande frequenti</span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-ink">
          Le risposte che cerchi,
          <span className="text-brand-blue"> scritte in parole semplici.</span>
        </h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          Tocca una domanda per leggere la risposta. Se non trovi ciò che cerchi, l'assistente
          virtuale (pulsante blu in basso a destra) risponde in pochi secondi, oppure scrivici su
          WhatsApp.
        </p>
      </Reveal>

      {SECTIONS.map((section, si) => (
        <Reveal key={section.title} className="mt-14">
          <h2 className="flex items-center gap-3 text-2xl font-extrabold text-brand-ink" data-testid={`faq-section-${si}`}>
            <span className="w-11 h-11 rounded-2xl bg-brand-blue text-white flex items-center justify-center">
              <section.icon className="w-5 h-5" />
            </span>
            {section.title}
          </h2>
          <div className="mt-6 space-y-3">
            {section.items.map((item, ii) => {
              const key = `${si}-${ii}`;
              return (
                <FaqItem
                  key={key}
                  item={item}
                  open={openKey === key}
                  onToggle={() => setOpenKey(openKey === key ? null : key)}
                  testId={`faq-item-${si}-${ii}`}
                />
              );
            })}
          </div>
        </Reveal>
      ))}

      <Reveal className="mt-16">
        <div className="rounded-3xl bg-brand-blue text-white p-8 sm:p-10 text-center" data-testid="faq-cta">
          <h2 className="text-2xl font-extrabold">Non hai trovato la risposta?</h2>
          <p className="mt-3 text-lg text-blue-100">
            Ogni situazione è diversa: raccontaci la tua, la prima consulenza è sempre gratuita.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/assistenza"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-blue font-bold text-lg px-8 h-14 transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="faq-cta-assistenza"
            >
              <Send className="w-5 h-5" />
              Richiedi assistenza gratuita
            </Link>
            <a
              href={waLink("Buongiorno, ho una domanda che non ho trovato nelle FAQ.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-bold text-lg px-8 h-14 transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="faq-cta-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              Scrivici su WhatsApp
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
